import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutStats, brandMilestones, whyChooseUs, faqItems } from '@/mocks/about';

function StatsSection() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-3">
            平台数据
          </h2>
          <p className="text-sm md:text-base text-foreground-600">
            我们用数据说话，吃瓜51网的每一步成长都有迹可循
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {aboutStats.map((stat, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8 text-center"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-4">
                <i className={`${stat.icon} text-xl`}></i>
              </div>
              <div className="text-3xl md:text-4xl font-heading text-foreground-900 mb-1">
                {stat.value}<span className="text-primary-500">{stat.suffix}</span>
              </div>
              <p className="text-sm text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrandStory() {
  return (
    <section className="py-14 md:py-20">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 text-xs font-medium border border-primary-300 text-primary-700 rounded-full mb-4 whitespace-nowrap">
            品牌故事
          </span>
          <h2 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-3">
            发展历程
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
            从2018年到今天，吃瓜51网与中文互联网的娱乐生态共同成长
          </p>
        </div>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 -translate-x-1/2 hidden md:block"></div>
          <div className="flex flex-col gap-8 md:gap-0">
            {brandMilestones.map((milestone, index) => {
              const isLeft = index % 2 === 0;
              return (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${isLeft ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                    {isLeft ? null : <div className="invisible">{' '}</div>}
                  </div>
                  <div className="flex items-center justify-center flex-shrink-0 relative">
                    <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center z-10">
                      <span className="text-xs font-bold text-background-50">{index + 1}</span>
                    </div>
                  </div>
                  <div className={`flex-1 ${!isLeft ? 'md:text-left' : 'md:text-right'}`}>
                    <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                      <span className="text-xs text-primary-600 font-bold whitespace-nowrap">{milestone.year}</span>
                      <h3 className="text-base font-semibold text-foreground-900 mt-1 mb-2">{milestone.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{milestone.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 text-xs font-medium border border-accent-300 text-accent-700 rounded-full mb-4 whitespace-nowrap">
            口碑声誉
          </span>
          <h2 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-3">
            为什么选择吃瓜51网
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
            用专业和热爱，打造中文互联网最值得信赖的娱乐资讯平台
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {whyChooseUs.map((item, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                <i className={`${item.icon} text-xl`}></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-900 mb-2 whitespace-nowrap">
                {item.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-20">
      <div className="max-w-[960px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 text-xs font-medium border border-primary-300 text-primary-700 rounded-full mb-4 whitespace-nowrap">
            常见问题
          </span>
          <h2 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-3">
            你关心的都在这里
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
            关于吃瓜51网的常见疑问，我们一一为你解答
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">{item.question}</span>
                <i
                  className={`ri-arrow-down-s-line text-foreground-500 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-2">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d96dmqvpg5pqhofcuns0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后再试';
        setStatus('error');
        setErrorMsg(serverMsg.includes('spam') ? '提交失败，请稍后再试' : serverMsg);
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查网络后重试');
    }
  };

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          <div className="w-full lg:w-5/12">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
              商务合作
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-4">
              联系我们
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
              无论是广告投放、品牌合作、内容授权，还是有爆料线索想提供，欢迎随时与我们联系。我们的团队将在2个工作日内回复您的咨询。
            </p>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">商务合作</p>
                  <p className="text-sm text-foreground-800">business@chigua51.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-send-plane-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">爆料投稿</p>
                  <p className="text-sm text-foreground-800">tips@chigua51.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">公司地址</p>
                  <p className="text-sm text-foreground-800">北京市朝阳区光华路SOHO 2期B座12层</p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-7/12">
            <form
              id="about-contact-form"
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8"
              data-readdy-form="about-contact"
            >
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-website-alt-field"
              />
              <h3 className="text-lg font-semibold text-foreground-900 mb-6">发送合作咨询</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-foreground-600 mb-1.5">姓名</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="请输入您的姓名"
                    className="w-full h-11 px-4 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="请输入您的邮箱"
                    className="w-full h-11 px-4 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-xs text-foreground-600 mb-1.5">合作类型</label>
                <select
                  name="cooperation_type"
                  className="w-full h-11 px-4 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  <option value="广告投放">广告投放</option>
                  <option value="品牌合作">品牌合作</option>
                  <option value="内容授权">内容授权</option>
                  <option value="爆料投稿">爆料投稿</option>
                  <option value="其他">其他</option>
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-xs text-foreground-600 mb-1.5">咨询内容</label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请简要描述您的合作需求（500字以内）"
                  className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full h-12 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </>
                ) : (
                  '发送咨询'
                )}
              </button>
              {status === 'success' && (
                <p className="mt-3 text-sm text-primary-600 font-medium text-center">
                  提交成功！我们将尽快与您联系，感谢关注吃瓜51网。
                </p>
              )}
              {status === 'error' && (
                <p className="mt-3 text-sm text-red-600 text-center">
                  {errorMsg}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '吃瓜51网',
    url: 'https://chigua51.com',
    description: '吃瓜51网(Chigua51.com)提供全网最新娱乐八卦资讯，聚焦51个热门话题，每日更新网红翻车现场与独家爆料。',
    email: 'business@chigua51.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '光华路SOHO 2期B座12层',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const webpageLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于我们 - 吃瓜51网',
    description: '了解吃瓜51网的品牌故事、发展历程、平台数据与商务合作联系方式。吃瓜51网，中文互联网最值得信赖的娱乐资讯平台。',
    url: 'https://chigua51.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '吃瓜51网',
      url: 'https://chigua51.com',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chigua51.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://chigua51.com/about' },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(orgLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
        />

        <section className="relative w-full overflow-hidden pt-16">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20warm%20lighting%2C%20editorial%20team%20working%20at%20desks%20with%20large%20monitors%20showing%20news%20content%2C%20collaborative%20atmosphere%2C%20natural%20light%20from%20large%20windows%2C%20warm%20amber%20and%20soft%20neutral%20tones%2C%20professional%20media%20company%20environment%2C%20cinematic%20wide%20angle%20photography%2C%20clean%20contemporary%20aesthetic&width=1600&height=600&seq=about-hero-bg&orientation=landscape"
              alt="吃瓜51网团队"
              className="w-full h-full object-cover object-top"
              title="吃瓜51网团队"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          </div>
          <div className="relative max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col items-center justify-center text-center min-h-[300px] md:min-h-[400px]">
              <nav className="flex items-center gap-2 text-xs text-background-100/70 mb-4 flex-wrap">
                <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">
                  首页
                </Link>
                <span>/</span>
                <span className="text-background-50">关于我们</span>
              </nav>
              <h1 className="text-3xl md:text-5xl font-heading text-background-50 mb-4">
                关于吃瓜51网
              </h1>
              <p className="text-sm md:text-lg text-background-100/80 max-w-2xl">
                吃瓜51网成立于2018年，致力于打造中文互联网最专业、最及时、最有趣的娱乐资讯聚合平台。聚焦51个热门话题，每日更新网红翻车现场与独家爆料。
              </p>
            </div>
          </div>
        </section>

        <StatsSection />
        <BrandStory />
        <WhyChooseUs />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />

      <style>{`
        .form-website-alt-field {
          position: absolute;
          left: -9999px;
          opacity: 0;
          height: 0;
          width: 0;
          overflow: hidden;
          pointer-events: none;
        }
      `}</style>
    </>
  );
}

import { Link } from 'react-router-dom';